import { KeyboardEventHandler, useCallback, useState, useEffect } from 'react';

import { ReactComponent as ChevronDownIcon } from '../../assets/icons/chevron-down.svg?icon';

export interface AccordionProperties {
  /** The header text which is always visible */
  header: string | React.ReactNode;
  /** The content that can be hidden */
  content: React.ReactNode;
  /** This controls the visibility of the content */
  isOpen?: boolean;
  /** Callback whenever the visibility state is toggled */
  onOpenChange?: (isOpen: boolean) => void;
  /** If header is toggle button, then clicking it expands/collapses content. */
  isHeaderToggleButton?: boolean;
  /** Useful for controlling the sizing of the collapse icon */
  collapseIconClasses?: string;
}

// eslint-disable-next-line max-lines-per-function
export const Accordion = (props: AccordionProperties) => {
  const {
    header,
    content,
    isOpen: defaultOpen = true,
    onOpenChange,
    isHeaderToggleButton = true,
    collapseIconClasses,
  } = props;

  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => setIsOpen(defaultOpen), [defaultOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      onOpenChange?.(!prev);
      return !prev;
    });
  }, [onOpenChange]);

  const handleKeyPress = useCallback<KeyboardEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();
      const triggers = ['Space', 'Enter'];
      if (triggers.includes(event.code)) {
        handleToggle();
      }
    },
    [handleToggle]
  );

  const formattedHeader = typeof header === 'string' ? <h3 className="py-6 text-gray-900">{header}</h3> : header;

  return (
    <div className="w-full">
      <div className="flex">
        {!isHeaderToggleButton && formattedHeader}
        <button
          className={`flex cursor-pointer select-none items-start justify-between ${
            isHeaderToggleButton ? 'w-full' : 'ml-auto'
          } ${typeof header === 'string' && !isHeaderToggleButton ? 'py-6' : ''}`}
          onClick={handleToggle}
          onKeyDown={handleKeyPress}
          aria-label={typeof header === 'string' ? header : 'accordion'}
          tabIndex={0}
        >
          {isHeaderToggleButton && formattedHeader}
          <ChevronDownIcon
            aria-label="Collapse content"
            className={
              `transition-all max-w-8 max-h-8 text-gray-900 fill-current ${isOpen ? 'rotate-180	' : 'rotate-0'}` +
              collapseIconClasses
            }
          />
        </button>
      </div>
      {isOpen && <div>{content}</div>}
    </div>
  );
};
export default Accordion;
